import { useMemo, useState, type ReactNode } from "react"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ArrowRightLeft, ExternalLink, Ticket, User } from "lucide-react"
import {
  CollectionView,
  type CollectionConfig,
} from "@/components/collection/CollectionView"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import { ApiError } from "@/core/api"
import { guestsKey } from "@/core/people"
import {
  formatMoney,
  ordersKey,
  ordersQueryOptions,
  reassignOrder,
  stripePaymentUrl,
  ticketsQueryOptions,
  type ReassignPreview,
  type TicketOrder,
} from "@/core/payments"
import { Field, GhostButton, PrimaryButton, TextInput } from "@/components/settings/primitives"
import { cn } from "@/core/utils"

export const Route = createFileRoute("/$orgSlug/$eventSlug/orders")({
  // ?order=<id> deep-links a specific order's detail (e.g. from the guest
  // record's ticket chip).
  validateSearch: (s: Record<string, unknown>): { order?: string } =>
    typeof s.order === "string" && s.order ? { order: s.order } : {},
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient.ensureQueryData(ordersQueryOptions(eventId)).catch(() => undefined)
      void context.queryClient.ensureQueryData(ticketsQueryOptions(eventId)).catch(() => undefined)
    })
  },
  component: OrdersView,
})

/** Rows minted before the compiler's runtime audit-default carry the literal
 *  1970 epoch placeholder — render those as absent, not as a real date. */
function dateOnly(iso?: string | null): string | null {
  if (!iso || iso.startsWith("1970-")) return null
  return iso.slice(0, 10)
}

const STATUS_BADGE: Record<string, string> = {
  paid: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400",
  refunded: "bg-plum-100 text-plum-700/70 dark:bg-white/8 dark:text-plum-200/50",
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={cn(
        "rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        STATUS_BADGE[status] ?? "bg-amber-500/12 text-amber-600 dark:text-amber-400",
      )}
    >
      {status}
    </span>
  )
}

function OrdersView() {
  const { eventId } = useActiveEvent()
  const { order: orderParam } = Route.useSearch()
  const [detail, setDetail] = useState<TicketOrder | null>(null)
  const [reassigning, setReassigning] = useState<TicketOrder | null>(null)

  const ordersQ = useQuery({ ...ordersQueryOptions(eventId ?? ""), enabled: !!eventId })
  // Deep link: open the requested order once the list arrives (once per param).
  const [openedParam, setOpenedParam] = useState<string | null>(null)
  const paramOrder =
    orderParam && orderParam !== openedParam
      ? ordersQ.data?.orders.find((o) => o.id === orderParam)
      : undefined
  if (paramOrder) {
    setOpenedParam(orderParam!)
    setDetail(paramOrder)
  }
  const ticketsQ = useQuery({ ...ticketsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const orders = ordersQ.data?.orders ?? []
  const testMode = ordersQ.data?.testMode ?? false
  const ticketName = useMemo(() => {
    const m = new Map<string, string>()
    for (const t of ticketsQ.data ?? []) m.set(t.id, t.name)
    return m
  }, [ticketsQ.data])

  const config = useMemo<CollectionConfig<TicketOrder>>(
    () => ({
      title: "Orders",
      getId: (o) => o.id,
      searchText: (o) =>
        `${o.buyerName ?? ""} ${o.buyerEmail} ${ticketName.get(o.ticketTypeId) ?? ""} ${o.status}`,
      columns: [
        {
          id: "buyer",
          header: "Buyer",
          grow: true,
          truncate: true,
          sortable: true,
          sortValue: (o) => (o.buyerName || o.buyerEmail).toLowerCase(),
          render: (o) => (
            <div className="min-w-0">
              <div className="truncate text-[13px] font-medium text-plum-900 dark:text-plum-50">
                {o.buyerName || o.buyerEmail}
              </div>
              <div className="truncate text-[12px] text-plum-700/55 dark:text-plum-200/40">
                {o.buyerEmail}
              </div>
            </div>
          ),
        },
        {
          id: "ticket",
          header: "Ticket",
          basis: "10rem",
          truncate: true,
          sortable: true,
          sortValue: (o) => ticketName.get(o.ticketTypeId) ?? "",
          render: (o) => ticketName.get(o.ticketTypeId) ?? "—",
        },
        {
          id: "amount",
          header: "Amount",
          basis: "6rem",
          align: "right",
          className: "tabular-nums",
          sortable: true,
          sortValue: (o) => o.amountCents,
          render: (o) => formatMoney(o.amountCents, o.currency),
        },
        {
          id: "status",
          header: "Status",
          basis: "6rem",
          sortable: true,
          sortValue: (o) => o.status,
          render: (o) => <StatusBadge status={o.status} />,
        },
        {
          id: "date",
          header: "Date",
          basis: "6.5rem",
          sortable: true,
          sortValue: (o) => o.paidAt ?? o.createdAt ?? "",
          render: (o) => dateOnly(o.paidAt) ?? dateOnly(o.createdAt) ?? "—",
        },
        {
          id: "actions",
          header: "",
          basis: "6.5rem",
          align: "right",
          render: (o) =>
            o.status === "paid" ? (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setReassigning(o)
                }}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium text-plum-700/70 transition hover:bg-plum-100/60 hover:text-plum-900 dark:text-plum-200/50 dark:hover:bg-white/5 dark:hover:text-plum-50"
              >
                <ArrowRightLeft className="h-3.5 w-3.5" /> Reassign
              </button>
            ) : null,
        },
      ],
      savedViews: [
        { id: "paid", label: "Paid", filter: (o) => o.status === "paid" },
        { id: "refunded", label: "Refunded", filter: (o) => o.status === "refunded" },
        {
          id: "incomplete",
          label: "Incomplete",
          filter: (o) => o.status !== "paid" && o.status !== "refunded",
        },
        { id: "all", label: "All", filter: () => true },
      ],
      defaultSortId: "date",
      defaultSortDir: "desc",
      onOpen: (o) => setDetail(o),
      emptyTitle: "No orders yet",
      emptyBlurb: "Paid ticket purchases from your public event page will show up here.",
      emptyIcon: <Ticket className="h-5 w-5" />,
    }),
    [ticketName],
  )

  return (
    <div className="relative flex h-full">
      <div className="min-w-0 flex-1">
        <CollectionView
          config={config}
          rows={orders}
          loading={ordersQ.isLoading && orders.length === 0}
          error={
            ordersQ.isError ? ((ordersQ.error as Error)?.message ?? "Failed to load orders") : null
          }
          selectable={false}
        />
      </div>
      {detail && !reassigning && (
        <OrderDetail
          order={detail}
          ticketName={ticketName.get(detail.ticketTypeId) ?? null}
          testMode={testMode}
          onReassign={() => setReassigning(detail)}
          onClose={() => setDetail(null)}
        />
      )}
      {reassigning && eventId && (
        <ReassignDialog
          eventId={eventId}
          order={reassigning}
          ticketName={ticketName.get(reassigning.ticketTypeId) ?? null}
          onClose={() => {
            setReassigning(null)
            setDetail(null)
          }}
        />
      )}
    </div>
  )
}

// ── Order detail ────────────────────────────────────────────────────────────
// Everything on the order row, organizer-framed: buyer, ticket, money split,
// timestamps, and deep links — the guest record in-app and the payment in the
// organizer's own Stripe dashboard (they're the merchant on direct charges).

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className="shrink-0 text-[12px] text-plum-700/55 dark:text-plum-200/40">{label}</span>
      <span className="min-w-0 text-right text-[13px] text-plum-900 dark:text-plum-50">
        {children}
      </span>
    </div>
  )
}

function OrderDetail({
  order,
  ticketName,
  testMode,
  onReassign,
  onClose,
}: {
  order: TicketOrder
  ticketName: string | null
  testMode: boolean
  onReassign: () => void
  onClose: () => void
}) {
  const { orgSlug, eventSlug } = Route.useParams()
  const stripeUrl = order.stripePaymentIntentId
    ? stripePaymentUrl(order.stripePaymentIntentId, testMode)
    : null

  return (
    <div className="absolute inset-0 z-40 flex items-start justify-center p-4 pt-[8vh]">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-plum-950/40 backdrop-blur-sm dark:bg-black/50"
      />
      <div className="relative z-10 flex max-h-full w-[min(94%,28rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-fluent-lg ring-1 ring-plum-200/50 dark:bg-surface-raised dark:ring-white/10">
        <div className="overflow-y-auto px-5 pt-8 pb-5">
          <div className="mb-0.5 flex items-center justify-between gap-3">
            <div className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
              Order #{order.id.slice(0, 8).toUpperCase()}
            </div>
            <StatusBadge status={order.status} />
          </div>
          <div className="mb-4 text-[12.5px] text-plum-700/55 dark:text-plum-200/40">
            {order.buyerName || order.buyerEmail}
          </div>

          <div className="divide-y divide-plum-200/40 dark:divide-white/8">
            <DetailRow label="Email">
              <span className="break-all">{order.buyerEmail}</span>
            </DetailRow>
            <DetailRow label="Ticket">
              {ticketName ?? "—"}
              {order.quantity > 1 ? ` × ${order.quantity}` : ""}
            </DetailRow>
            <DetailRow label="Total paid">
              <span className="tabular-nums">{formatMoney(order.amountCents, order.currency)}</span>
            </DetailRow>
            {order.applicationFeeCents > 0 && (
              <DetailRow label="Attend fee (included)">
                <span className="tabular-nums">
                  {formatMoney(order.applicationFeeCents, order.currency)}
                </span>
              </DetailRow>
            )}
            {dateOnly(order.paidAt) && <DetailRow label="Paid">{dateOnly(order.paidAt)}</DetailRow>}
            {dateOnly(order.createdAt) && (
              <DetailRow label="Created">{dateOnly(order.createdAt)}</DetailRow>
            )}
            {order.guestId && (
              <DetailRow label="Guest">
                <Link
                  to="/$orgSlug/$eventSlug/person/$guestId"
                  params={{ orgSlug, eventSlug, guestId: order.guestId }}
                  className="inline-flex items-center gap-1 font-medium text-[var(--accent)] hover:underline"
                >
                  <User className="h-3.5 w-3.5" /> View guest record
                </Link>
              </DetailRow>
            )}
            {stripeUrl && (
              <DetailRow label="Payment">
                <a
                  href={stripeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-[var(--accent)] hover:underline"
                >
                  View in Stripe <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </DetailRow>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 pt-3 pb-2">
          <GhostButton onClick={onClose}>Close</GhostButton>
          {order.status === "paid" && (
            <PrimaryButton onClick={onReassign}>
              <ArrowRightLeft className="mr-1 h-4 w-4" /> Reassign ticket
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Reassign dialog ─────────────────────────────────────────────────────────
// Two-step: capture the new holder → server dry-run preview → confirm. The
// real run cancels the current holder's guest row and registers + emails the
// new one, so nothing mutates until the organizer has seen exactly that.

function ReassignDialog({
  eventId,
  order,
  ticketName,
  onClose,
}: {
  eventId: string
  order: TicketOrder
  ticketName: string | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [preview, setPreview] = useState<ReassignPreview | null>(null)

  const input = { email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim() || undefined }
  const valid = input.firstName.length > 0 && /.+@.+\..+/.test(input.email)

  const dryRun = useMutation({
    mutationFn: () => reassignOrder(order.id, input, true),
    onSuccess: (r) => {
      if ("preview" in r) setPreview(r.preview)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't preview reassignment"),
  })

  const commit = useMutation({
    mutationFn: () => reassignOrder(order.id, input, false),
    onSuccess: () => {
      toast.success(`Ticket reassigned to ${input.firstName}`)
      void qc.invalidateQueries({ queryKey: ordersKey(eventId) })
      void qc.invalidateQueries({ queryKey: guestsKey(eventId) })
      onClose()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Reassignment failed"),
  })

  return (
    <div className="absolute inset-0 z-40 flex items-start justify-center p-4 pt-[8vh]">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-plum-950/40 backdrop-blur-sm dark:bg-black/50"
      />
      <div className="relative z-10 flex max-h-full w-[min(94%,28rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-fluent-lg ring-1 ring-plum-200/50 dark:bg-surface-raised dark:ring-white/10">
        <div className="overflow-y-auto px-5 pt-8 pb-5">
          <div className="mb-1 text-[15px] font-semibold text-plum-900 dark:text-plum-50">
            Reassign ticket
          </div>
          <div className="mb-4 text-[12.5px] text-plum-700/55 dark:text-plum-200/40">
            {ticketName ?? "Ticket"} · {formatMoney(order.amountCents, order.currency)} · currently{" "}
            {order.buyerName || order.buyerEmail}
          </div>

          {preview ? (
            <div className="space-y-2 rounded-xl bg-plum-100/50 p-3 text-[13px] text-plum-800/90 dark:bg-white/5 dark:text-plum-100/80">
              <div>
                <span className="font-semibold">{preview.from ?? "Current holder"}</span> →{" "}
                <span className="font-semibold">{preview.to}</span> ({preview.toEmail})
              </div>
              {preview.willCancelCurrentGuest && (
                <div>The current holder's registration will be cancelled.</div>
              )}
              <div>{preview.to} will get a confirmation email with their ticket.</div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name">
                  <TextInput autoFocus value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </Field>
                <Field label="Last name">
                  <TextInput value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </Field>
              </div>
              <Field label="Email">
                <TextInput
                  type="email"
                  value={email}
                  placeholder="new.holder@example.com"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 pt-3 pb-2">
          <GhostButton onClick={preview ? () => setPreview(null) : onClose}>
            {preview ? "Back" : "Cancel"}
          </GhostButton>
          {preview ? (
            <PrimaryButton onClick={() => !commit.isPending && commit.mutate()} disabled={commit.isPending}>
              {commit.isPending ? "Reassigning…" : "Confirm reassignment"}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              onClick={() => valid && !dryRun.isPending && dryRun.mutate()}
              disabled={!valid || dryRun.isPending}
            >
              {dryRun.isPending ? "Checking…" : "Preview"}
            </PrimaryButton>
          )}
        </div>
      </div>
    </div>
  )
}
